// components/Layout/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useMyProfile } from '../../hooks/useMyProfile';
import {
    Bars3Icon,
    XMarkIcon,
    UserCircleIcon,
    ArrowRightOnRectangleIcon,
    ChartBarIcon,
    DocumentTextIcon,
} from '@heroicons/react/24/outline';
import logo from '../../assets/IqKontrolLogo.ico'
import logoNuevo from '../../assets/iqKontrolNuevo.jpeg'

export const Header = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { user, logout, isAuthenticated } = useAuth();
	const { profile, refetch: refetchProfile } = useMyProfile();
	const { 
		canManageUsers, 
		canManageCompanies, 
		canManageActas, 
		canViewMetricas 
	} = usePermissions();
  	const navigate = useNavigate();
  	const location = useLocation();
	
	useEffect(() => {
            if (isAuthenticated) {
                refetchProfile();
            }
        }, [location.pathname, isAuthenticated]);

	const handleLogout = () => {
		logout();
		navigate('/login');
		setIsMenuOpen(false);
	};

	const handleNavigation = (path) => {
		navigate(path);
		setIsMenuOpen(false);
	};

	const closeMenu = () => {
		setIsMenuOpen(false);
	};

	const getDisplayName = () => {
		if (user?.first_name && user?.last_name) {
		    return `${user.first_name} ${user.last_name}`;
		}
		return user?.username || 'Usuario';
	};

	const getActiveClass = (path) => {
		return location.pathname === path 
		? 'text-blue-600 border-b-2 border-blue-600' 
		: 'text-gray-700 hover:text-blue-600';
	};

	const getMobileActiveClass = (path) => {
		return location.pathname === path 
		? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' 
		: 'text-gray-700 hover:bg-gray-50';
	};
	
	const photoUrl = profile?.photo_url || user?.photo_url;

  	return (
    	<header className="bg-white shadow-sm border-b relative z-50">
      		<div className="container mx-auto px-4">
        		<div className="flex justify-between items-center h-16">
          			{/* Logo */}
					<Link to="/dashboard" className="flex items-center space-x-2">
            <img src={logoNuevo} alt="" className='w-60 h-14'/>
					</Link>

					{/* Desktop Navigation */}
					<nav className="hidden md:flex items-center space-x-6">
						{isAuthenticated ? (
						<>
							<Link
								to="/dashboard"
								className={`font-medium transition-colors duration-200 ${getActiveClass('/dashboard')}`}
							>
								Dashboard
							</Link>
							<Link
								to="/my-profile"
								className={`font-medium transition-colors duration-200 ${getActiveClass('/my-profile')}`}
							>
								Mi Perfil
							</Link>
							
							{canManageActas && (
							<Link
								to="/actas"
								className={`font-medium transition-colors duration-200 ${getActiveClass('/actas')}`}
							>
								Actas
							</Link>
							)}
							
							{canViewMetricas && (
							<Link
								to="/metricas"
								className={`font-medium transition-colors duration-200 ${getActiveClass('/metricas')}`}
							>
								<div className="flex items-center space-x-1">
								<ChartBarIcon className="h-4 w-4" />
								<span>Métricas</span>
								</div>
							</Link>
							)}
							
							{canManageUsers && (
							<Link
								to="/users"
								className={`font-medium transition-colors duration-200 ${getActiveClass('/users')}`}
							>
								Usuarios
							</Link>
							)}
							
							{canManageCompanies && (
							<Link
								to="/companies"
								className={`font-medium transition-colors duration-200 ${getActiveClass('/companies')}`}
							>
								Empresas
							</Link>
							)}
						</>
						) : (
						<>
							<Link
							to="/login"
							className="text-gray-700 hover:text-blue-600 font-medium"
							>
							Login
							</Link>
							<Link
							to="/register"
							className="text-gray-700 hover:text-blue-600 font-medium"
							>
							Registro
							</Link>
						</>
						)}
					</nav>

          {/* User Menu (Desktop) */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <div className="flex items-center space-x-3">
                  <div className="text-right hidden lg:block">
                    <p className="font-medium text-gray-900 text-sm">{getDisplayName()}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                  </div>
                  <div className="relative group">
                    <button className="flex items-center space-x-1 p-2 rounded-full">
                      { photoUrl ? (
                        <img 
				src={photoUrl} 
				alt="{getDisplayName()}" 
				className='w-12 h-12 rounded-full'
				onError={(e) => {
					e.target.style.display = 'none';
				}}
			/>
                      ) : (    
                        <UserCircleIcon className="h-8 w-8 text-gray-400" />
                      )}
                    </button>
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 hidden group-hover:block border border-gray-200">
                      <Link
                        to="/my-profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Mi Perfil
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                      >
                        <div className="flex items-center space-x-2">
                          <ArrowRightOnRectangleIcon className="h-4 w-4" />
                          <span>Cerrar Sesión</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-gray-100 transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t bg-white absolute left-0 right-0 top-16 shadow-lg z-50">
            <nav className="flex flex-col space-y-1 px-4">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => handleNavigation('/dashboard')}
                    className={`text-left py-3 px-3 rounded-md transition-colors duration-200 ${getMobileActiveClass('/dashboard')}`}
                  >
                    Dashboard
                  </button>
                  
                  <button
                    onClick={() => handleNavigation('/my-profile')}
                    className={`text-left py-3 px-3 rounded-md transition-colors duration-200 ${getMobileActiveClass('/my-profile')}`}
                  >
                    Mi Perfil
                  </button>
                  
                  {canManageActas && (
                    <button
                      onClick={() => handleNavigation('/actas')}
                      className={`text-left py-3 px-3 rounded-md transition-colors duration-200 ${getMobileActiveClass('/actas')}`}
                    >
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-5 w-5" />
                        <span>Actas</span>
                      </div>
                    </button>
                  )}
                  
                  {canViewMetricas && (
                    <button
                      onClick={() => handleNavigation('/metricas')}
                      className={`text-left py-3 px-3 rounded-md transition-colors duration-200 ${getMobileActiveClass('/metricas')}`}
                    >
                      <div className="flex items-center space-x-2">
                        <ChartBarIcon className="h-5 w-5" />
                        <span>Métricas</span>
                      </div>
                    </button>
                  )}
                  
                  {canManageUsers && (
                    <button
                      onClick={() => handleNavigation('/users')}
                      className={`text-left py-3 px-3 rounded-md transition-colors duration-200 ${getMobileActiveClass('/users')}`}
                    >
                      Usuarios
                    </button>
                  )}
                  
                  {canManageCompanies && (
                    <button
                      onClick={() => handleNavigation('/companies')}
                      className={`text-left py-3 px-3 rounded-md transition-colors duration-200 ${getMobileActiveClass('/companies')}`}
                    >
                      Empresas
                    </button>
                  )}
                  
                  {/* User Info in Mobile */}
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-lg">
                      <button className="flex items-center space-x-1 p-2 rounded-full">
                      { photoUrl ? (
                        <img 
				src={photoUrl} 
				alt="{getDisplayName()}" 
				className='w-12 h-12 rounded-full'
				onError={(e) => {
                            		e.target.style.display = 'none';
                          	}}
			/>
                      ) : (    
                        <UserCircleIcon className="h-8 w-8 text-gray-400" />
                      )}
                    </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{getDisplayName()}</p>
                        <p className="text-sm text-gray-500 capitalize truncate">{user?.role}</p>
                        <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center space-x-2 py-3 px-4 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200 border border-red-200"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      <span>Cerrar Sesión</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleNavigation('/login')}
                    className="text-left py-3 px-3 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => handleNavigation('/register')}
                    className="text-left py-3 px-3 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    Registro
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>

      {/* Overlay for mobile menu */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={closeMenu}
        />
      )}
    </header>
  );
};
